import React, { useEffect, useState } from "react";
import { Control, Controller, FormState, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useApolloClient, useLazyQuery } from "@apollo/client";
import { z } from "zod";

import { Checkbox, FormGroup, Input, Select, LabelPlacement } from "@cofano/cck";

import DateRangePicker from "components/cck/DateRangePicker";
import FormField from "components/cck/FormField";

import RelationSelect from "view/relations/components/selects/RelationSelect";
import LinkableContractSelect from "view/contractsNew/components/selects/LinkableContractSelect";

import { Relation, Ship } from "types/index";
import { RelationType, Voyage } from "__generated__/graphql";
import { gql } from "__generated__/gql";

import {
	determineAutomaticCreditor,
	determineCharterValidity,
	getCharterSubtypeColor,
} from "view/charters/utils/CharterUtils";
import { CreateOrExistingPurchaseCharterSchema, ExistingCharterSchema } from "view/charters/utils/charter.schema";
import CharterSelect from "view/charters/components/selects/CharterSelect";

import {
	CreateOrSelectCharterFormCargoFragment,
} from "__generated__/view/charters/graphql/charter.fragments.generated";
import { CharterSubtype, CharterType } from "__generated__/enums";
import { GetContractsShortDocument } from "__generated__/view/contractsNew/graphql/contract.queries.generated";

export type CustomShip = Pick<Ship, "id" | "owner" | "defaultCharterSubtype" | "name"> & { operator?: Pick<Relation, "id" | "applicationRelation"> | null };
export type CustomVoyage = Pick<Voyage, "id">;
export type CustomCharterSubtype = "EXISTING" | Exclude<CharterSubtype, CharterSubtype.Supplier>;

export type ExistingCharterType = z.infer<typeof ExistingCharterSchema>;
export type CreateOrExistingPurchaseCharterForm = z.input<typeof CreateOrExistingPurchaseCharterSchema>;

export const charterDefaultValues = {
	type: CharterType.Purchase,
	subtype: "EXISTING" as CustomCharterSubtype,
	createContract: true,
	new: {
		id: null,
		charterNumber: "",
		creditor: null,
		debtor: null,
		validBetween: null,
		contract: null,
	},
	existing: null,
} as const;

const useCreateOrExistingCharterFormFields = <T extends CreateOrExistingPurchaseCharterForm,>({
	control: rawControl,
	setValue: rawSetValue,
	watch: rawWatch,
	formState: rawFormState,
	ship,
	voyage,
	cargos,
	customStart,
	customEnd,
	labelPlacement = "left-center",
	createContractDisabled = false,
	disableExistingSelectOption = false,
}: {
	control: Control<T>;
	setValue: UseFormSetValue<T>;
	watch: UseFormWatch<T>;
	formState: FormState<T>;
	ship?: CustomShip;
	voyage?: CustomVoyage;
	cargos?: CreateOrSelectCharterFormCargoFragment[];
	customStart?: number;
	customEnd?: number;
	labelPlacement?: LabelPlacement;
	createContractDisabled?: boolean;
	disableExistingSelectOption?: boolean;
}) => {
	const control = rawControl as Control<CreateOrExistingPurchaseCharterForm>;
	const setValue = rawSetValue as UseFormSetValue<CreateOrExistingPurchaseCharterForm>;
	const watch = rawWatch as UseFormWatch<CreateOrExistingPurchaseCharterForm>;
	const formState = rawFormState as FormState<CreateOrExistingPurchaseCharterForm>;

	const client = useApolloClient();

	const [ getAllLinkableContracts ] = useLazyQuery(GetContractsShortDocument);

	const [ defaultDebtor, setDefaultDebtor ] = useState<Pick<Relation, "id" | "name" | "applicationRelation"> | null>(null);

	const [ charter ] = watch([ "charter" ]);

	const getStartEnd = () => {

		if (!cargos) {
			return {
				start: customStart,
				end: customEnd,
			};
		}

		const sortedCargos = cargos.sort((first, second) => {
			return (first.firstLoadingAction?.start ?? 0) - (second.firstLoadingAction?.start ?? 0);
		}) ?? [];

		return {
			start: sortedCargos.length > 0 ? sortedCargos[0].firstLoadingAction?.start : undefined,
			end: sortedCargos.length > 0 ? sortedCargos.at(-1)?.lastDischargingAction?.end : undefined,
		};
	};

	const resetFieldsBasedOnShip = () => {
		if (ship) {
			const nextType = ship.defaultCharterSubtype || charter.subtype || "EXISTING";
			setValue("charter.subtype", nextType as CustomCharterSubtype);
			setValue("charter.new.creditor", determineAutomaticCreditor(ship));
			setValue("charter.new.debtor", defaultDebtor);
			setValue("charter.new.contract", undefined);
			setValue("charter.existing", null);
		} else {
			setValue("charter", charterDefaultValues);
		}
	};

	const getDefaultDebtor = async() => {
		const result = await client.query({ query: applicationRelationQuery });

		const relation = result.data.applicationRelation;

		const field = {
			id: relation.id,
			name: relation.name,
			applicationRelation: relation.applicationRelation ?? false,
		};

		setDefaultDebtor(field);
		setValue("charter.new.debtor", field);
	};

	const { start, end } = getStartEnd();

	const setDefaultContract = () => {
		if (charter.subtype === "EXISTING") {
			return;
		}

		getAllLinkableContracts({
			variables: {
				search: {
					charterType: charter.type,
					charterSubtypes: [ charter.subtype ],
					creditors: charter.new?.creditor?.id ? [ charter.new.creditor.id ] : [],
					debtors: charter.new?.debtor?.id ? [ charter.new.debtor.id ] : [],
					isParentOrNoInheritance: true,
					validFrom: start,
				},
				pagination: {
					size: 999,
					page: 0,
				},
			},
		})
			.then(({ data }) => {
				const contracts = data?.contracts.content ?? [];

				const parentContracts = contracts.filter(c => c.isParentContract);

				if (parentContracts.length === 1) {
					const contract = parentContracts[0];
					setValue("charter.new.contract", {
						...contract,
						description: contract.description ?? "",
						contractNumber: contract.contractNumber ?? "",
						isParentContract: contract.isParentContract ?? false,
					});
					if (contract.validFrom && contract.validUntil) {
						setValue("charter.new.validBetween.0", new Date(contract.validFrom));
						setValue("charter.new.validBetween.1", new Date(contract.validUntil));
					}
				}
			});
	};

	useEffect(() => {
		resetFieldsBasedOnShip();
	}, [ ship?.id, voyage?.id ]);

	useEffect(() => {
		getDefaultDebtor();
	}, []);

	useEffect(() => {
		if (charter?.type) {
			setDefaultContract();
		}

	}, [ ship?.id, charter.type ]);

	const currentCharterFields = charter.subtype === "EXISTING" ? charter.existing : charter.new;
	const currentCharterType = charter.subtype === "EXISTING" ? charter.existing?.type : charter.type;
	const currentCharterSubtype = charter.subtype === "EXISTING" ? charter.existing?.subtype : charter.subtype;
	const [ validFrom, validUntil ] = determineCharterValidity(charter);

	return {
		charterInput: {
			id: charter.subtype === "EXISTING" ? charter.existing?.id : undefined,
			type: currentCharterType,
			subtype: currentCharterSubtype,
			charterNumber: currentCharterFields?.charterNumber || undefined,
			creditor: currentCharterFields?.creditor ? {
				id: currentCharterFields.creditor.id,
			} : undefined,
			debtor: currentCharterFields?.debtor ? {
				id: currentCharterFields.debtor.id,
			} : undefined,
			validFrom: validFrom,
			validUntil: validUntil,
			voyages: voyage ? [ { id: voyage.id } ] : undefined,
			contract: (currentCharterType === CharterType.Purchase && (currentCharterSubtype === CharterSubtype.Time || currentCharterSubtype === CharterSubtype.Contract) && (charter.subtype !== "EXISTING") && charter.new?.contract) ? {
				id: charter.new.contract.id,
			} : undefined,
		},
		createContract: charter.type === CharterType.Purchase && charter.subtype === CharterSubtype.Spot ? charter.createContract : false,
		charterFields: (
			<Fields
				control={control}
				setValue={setValue}
				watch={watch}
				formState={formState}
				ship={ship}
				voyage={voyage}
				start={start}
				end={end}
				labelPlacement={labelPlacement}
				createContractDisabled={createContractDisabled}
				disableExistingSelectOption={disableExistingSelectOption} />
		),
		start,
		end,
	};
};

// eslint-disable-next-line react-refresh/only-export-components
const Fields = ({
	control,
	setValue,
	watch,
	formState,
	ship,
	voyage,
	start,
	end,
	labelPlacement,
	createContractDisabled,
	disableExistingSelectOption,
}: {
	control: Control<CreateOrExistingPurchaseCharterForm>;
	setValue: UseFormSetValue<CreateOrExistingPurchaseCharterForm>;
	watch: UseFormWatch<CreateOrExistingPurchaseCharterForm>;
	formState: FormState<CreateOrExistingPurchaseCharterForm>;
	ship?: CustomShip;
	voyage?: CustomVoyage;
	start?: number | null;
	end?: number | null;
	labelPlacement?: LabelPlacement;
	createContractDisabled?: boolean;
	disableExistingSelectOption?: boolean;
}) => {

	const [ charterType, charterSubtype, creditor, debtor, contract, validBetween ] = watch([ "charter.type", "charter.subtype", "charter.new.creditor", "charter.new.debtor", "charter.new.contract", "charter.new.validBetween" ]);

	const isPurchaseCharter = charterType === CharterType.Purchase;
	const canCreateContract = isPurchaseCharter && charterSubtype === CharterSubtype.Spot && !createContractDisabled;
	const canLinkContract = isPurchaseCharter && (charterSubtype === CharterSubtype.Time || charterSubtype === CharterSubtype.Contract);

	const hasShip = !!ship;

	return (
		<React.Fragment>
			<FormGroup label={intl.formatMessage({ id: "TYPE" })} labelPlacement={labelPlacement} overflow="hidden">
				<Controller
					name="charter.subtype"
					control={control}
					render={({ field }) => (
						<Select
							{...field}
							fetchOptions={[ "EXISTING", CharterSubtype.Spot, CharterSubtype.Time, CharterSubtype.Contract ].filter(it => {
								if (disableExistingSelectOption) {
									return it !== "EXISTING";
								} else {
									return true;
								}
							})}
							onChange={(option) => {
								if (option) {
									setValue("charter.new.contract", undefined);
									setValue("charter.existing", null);
									field.onChange(option);
								}
							}}
							getOptionLabel={option => intl.formatMessage({ id: option })}
							getOptionColor={option => getCharterSubtypeColor(option as CharterSubtype)}
							clearable={false}
							disabled={!hasShip}
							disableSearch
						/>
					)}
				/>
			</FormGroup>

			{(charterSubtype === "EXISTING") ? (
				<FormGroup
					label={intl.formatMessage({ id: "CHARTER" })}
					labelPlacement={labelPlacement}
					overflow="hidden"
					required>
					<FormField errors={formState.errors} name={"charter.existing"}>
						<Controller
							name="charter.existing"
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<React.Fragment>
									<CharterSelect
										{...field}
										value={field.value?.id ? field.value : null}
										search={{
											charterType: CharterType.Purchase,
											charterSubtypes: [ CharterSubtype.Time ],
											creditors: [ ship?.operator?.id, ship?.owner?.id ],
											hasContract: true,
											start: start ?? undefined,
											end: end ?? undefined,
											ship: ship,
											voyage: voyage,
										}}
										disabled={!hasShip}
										withoutCreate />
								</React.Fragment>
							)} />
					</FormField>
				</FormGroup>
			) : (
				<React.Fragment>
					<FormGroup
						label={intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" })}
						labelPlacement={labelPlacement}
						tooltip={intl.formatMessage({ id: "CHARTER_ORDER_REFERENCE_TIPS" })}
						overflow="hidden">
						<Controller
							name="charter.new.charterNumber"
							control={control}
							render={({ field }) => <Input {...field} />}
						/>
					</FormGroup>

					<FormGroup
						label={intl.formatMessage({ id: "CREDITOR" })}
						labelPlacement={labelPlacement}
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name={"charter.new.creditor"}>
							<Controller
								name="charter.new.creditor"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<RelationSelect
										{...field}
										types={[ RelationType.Operator ]}
										additionalRelations={ship?.owner ? [ ship.owner ] : undefined}
										onChange={option => {
											setValue("charter.new.contract", undefined);
											field.onChange(option);
										}}
										groupBy={(option) => {
											if ([ ship?.operator?.id, ship?.owner?.id ].includes(option.id)) {
												return ship?.name ?? intl.formatMessage({ id: "SHIP" });
											}
											return intl.formatMessage({ id: "OPERATORS" });
										}}
									/>
								)} />
						</FormField>
					</FormGroup>

					<FormGroup
						label={intl.formatMessage({ id: "DEBTOR" })}
						labelPlacement={labelPlacement}
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name={"charter.new.debtor"}>
							<Controller
								name="charter.new.debtor"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<RelationSelect
										{...field}
										onChange={option => {
											setValue("charter.new.contract", undefined);
											field.onChange(option);
										}}
										types={[ RelationType.ApplicationRelation ]} />
								)} />
						</FormField>
					</FormGroup>

					{canCreateContract && (
						<FormGroup
							label={intl.formatMessage({ id: "CREATE_CONTRACT_FOR_CHARTER" })}
							labelPlacement={labelPlacement}
							overflow="visible">
							<Controller
								name="charter.createContract"
								control={control}
								render={({ field }) => (
									<Checkbox
										{...field}
										value={undefined}
										checked={field.value}
										onChange={event => field.onChange(event.target.checked)} />
								)} />
						</FormGroup>
					)}

					{canLinkContract && (
						<FormGroup label={intl.formatMessage({ id: "CONTRACT" })} labelPlacement={labelPlacement} overflow="hidden">
							<Controller
								name="charter.new.contract"
								control={control}
								render={({ field: { value, onChange } }) => (
									<LinkableContractSelect
										value={value}
										onChange={(option) => {
											if (option != null && option.isParentContract) {
												if (option.validFrom) {
													setValue("charter.new.validBetween.0", new Date(option.validFrom));
												}
												if (option.validUntil) {
													setValue("charter.new.validBetween.1", new Date(option.validUntil));
												}
											} else {
												setValue("charter.new.validBetween", undefined);
											}
											onChange(option);
										}}
										additionalSearchOptions={{
											validFrom: start,
											charterType: charterType,
											charterSubtypes: [ charterSubtype ],
											creditorId: creditor?.id,
											debtorId: debtor?.id,
											shipId: charterSubtype === CharterSubtype.Time ? ship?.id : undefined,
										}}
									/>
								)}
							/>
						</FormGroup>
					)}

					{charterSubtype === CharterSubtype.Time && (
						<FormGroup
							label={intl.formatMessage({ id: "VALID_BETWEEN" })}
							labelPlacement={labelPlacement}
							overflow="hidden"
							required>
							<FormField
								errors={formState.errors}
								name="charter.new.validBetween"
								validation={intl.formatMessage({ id: "SELECT_VALID_UNTIL" })}>
								<Controller
									name="charter.new.validBetween"
									control={control}
									rules={{
										required: true,
										validate: (value) => !!value?.[1],
									}}
									render={({ field }) => (
										<DateRangePicker
											startDate={validBetween?.[0]}
											endDate={validBetween?.[1]}
											disabled={field.disabled || (contract?.isParentContract ?? false)}
											onChange={window => {
												field.onChange(window);
											}}
										/>
									)}
								/>
							</FormField>
						</FormGroup>
					)}

				</React.Fragment>
			)}
		</React.Fragment>
	);
};

const applicationRelationQuery = gql(`#graphql
	query applicationRelation_charterFormFields {
		applicationRelation {
			id
			name
			email
			applicationRelation
		}
	}
`);

export default useCreateOrExistingCharterFormFields;
